import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { InlineSpinner } from '../components/UI';
import AuthShell from '../components/AuthShell';

const RESET_ERROR = 'This password reset link is invalid or has expired.';
const STAFF_LOGIN_URL = 'https://cloud.account.easb.edu.sg/';

function validRecoveryIdentifier(value) {
  return value.length >= 3 && value.length <= 80 && /^[a-zA-Z0-9+()\-\s._@]+$/.test(value);
}

export default function PasswordRecoveryPage({ reset = false }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [identifier, setIdentifier] = useState('');
  const [form, setForm] = useState({ new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(reset);
  const [tokenValid, setTokenValid] = useState(!reset);
  const [sent, setSent] = useState(false);
  const [staffRedirected, setStaffRedirected] = useState(false);
  const [staffResetUrl, setStaffResetUrl] = useState(STAFF_LOGIN_URL);
  const [staffPopupBlocked, setStaffPopupBlocked] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSent, setResendSent] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (!reset) return undefined;
    if (!token) {
      setCheckingToken(false);
      setTokenValid(false);
      return undefined;
    }
    let active = true;
    api.get('/auth/student/reset-password/validate', { params: { token } })
      .then(() => { if (active) setTokenValid(true); })
      .catch(() => { if (active) setTokenValid(false); })
      .finally(() => { if (active) setCheckingToken(false); });
    return () => { active = false; };
  }, [reset, token]);

  useEffect(() => {
    if (!resendCooldown) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown(value => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleRequest = async e => {
    e.preventDefault();
    setError('');
    const normalized = identifier.trim();
    if (!validRecoveryIdentifier(normalized)) {
      setError('Enter a valid username or mobile number.');
      return;
    }
    setLoading(true);
    try {
      const identification = await api.post('/auth/password-recovery/identify', { identifier: normalized });
      if (identification.data?.account_type === 'staff') {
        const resetUrl = identification.data.redirect_url || STAFF_LOGIN_URL;
        const popup = window.open(resetUrl, '_blank', 'noopener,noreferrer');
        setStaffResetUrl(resetUrl);
        setStaffPopupBlocked(!popup);
        setStaffRedirected(true);
        return;
      }
      if (identification.data?.account_type === 'unknown') {
        setError('We could not find an active staff account with that username. Check it and try again.');
        return;
      }
      await api.post('/auth/student/forgot-password', { phone: normalized });
      setSent(true);
      setResendCooldown(60);
      setResendSent(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to process the request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (loading || resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/student/forgot-password', { phone: identifier.trim() });
      setResendCooldown(60);
      setResendSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to resend the email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentIdentifier = () => {
    setSent(false);
    setStaffRedirected(false);
    setStaffPopupBlocked(false);
    setStaffResetUrl(STAFF_LOGIN_URL);
    setIdentifier('');
    setResendCooldown(0);
    setResendSent(false);
    setError('');
  };

  const handleReset = async e => {
    e.preventDefault();
    setError('');
    if (form.new_password.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('The passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/student/reset-password', { token, new_password: form.new_password });
      setComplete(true);
    } catch (err) {
      setError(err.response?.data?.message || RESET_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const errorBox = error && (
    <div role="alert" aria-live="polite" className="lg-err">
      <i className="bi bi-exclamation-circle-fill" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
      {error}
    </div>
  );

  const header = (iconClass, title, subtitle, tone = '') => (
    <>
      <div className={`lg-state-icon ${tone}`} aria-hidden="true"><i className={`bi ${iconClass}`} /></div>
      <h1 className="lg-title">{title}</h1>
      {subtitle && <p className="lg-sub">{subtitle}</p>}
    </>
  );

  let content;
  if (!reset && staffRedirected) {
    content = (
      <>
        {header('bi-person-check-fill', 'Staff Password Reset')}
        <div role="status" className="lg-banner">
          <i className="bi bi-info-circle-fill" aria-hidden="true" />
          <span>
            {staffPopupBlocked ? 'The reset page could not open automatically. Use the button below to open it.' : 'A new tab should now be open for the staff password reset.'} Enter your staff username there and follow the password reset instructions.
          </span>
        </div>
        <div className="lg-row">
          <a href={staffResetUrl} target="_blank" rel="noreferrer" className="lg-btn" style={{ width: 'auto' }}>
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Open Staff Reset Page
          </a>
          <button type="button" className="lg-textbtn" onClick={handleUseDifferentIdentifier}>Use a Different Identifier</button>
        </div>
      </>
    );
  } else if (!reset && sent) {
    content = (
      <>
        {header('bi-envelope-check-fill', 'Check Your Inbox', null, 'ok')}
        <div role="status" className="lg-banner ok">
          <i className="bi bi-check-circle-fill" aria-hidden="true" />
          <span>
            {resendSent ? 'A new password reset link has been sent to the registered email.' : 'If an active student account uses that mobile number, a password reset link has been sent to its registered email.'} Check your inbox and spam folder.
          </span>
        </div>
        {errorBox}
        <div className="lg-row">
          <button type="button" className="lg-btn-outline" onClick={handleResend} disabled={loading || resendCooldown > 0}>
            {loading
              ? <><InlineSpinner size={14} /> Sending…</>
              : <><i className="bi bi-arrow-repeat" aria-hidden="true" /> {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Email'}</>}
          </button>
          <button type="button" className="lg-textbtn" onClick={handleUseDifferentIdentifier}>Use a Different Identifier</button>
        </div>
      </>
    );
  } else if (!reset) {
    content = (
      <>
        {header('bi-envelope-at', 'Forgot Your Password?', 'Enter your username or mobile number. We will guide you to the correct password reset method.')}
        {errorBox}
        <form onSubmit={handleRequest} noValidate>
          <div className="lg-field">
            <label htmlFor="account-recovery-identifier" className="lg-label">Username or Mobile Number</label>
            <input id="account-recovery-identifier" className="lg-input" type="text" autoComplete="username"
              placeholder="Enter username or mobile number" value={identifier}
              onChange={e => setIdentifier(e.target.value)} disabled={loading} />
          </div>
          <div className="lg-actions">
            <button type="submit" className="lg-btn" disabled={loading}>
              {loading
                ? <><InlineSpinner size={15} /> Sending Link…</>
                : <><i className="bi bi-send" aria-hidden="true" /> Send Reset Link</>}
            </button>
          </div>
        </form>
      </>
    );
  } else if (checkingToken) {
    content = (
      <>
        {header('bi-shield-lock', 'Set a New Password')}
        <div className="lg-checking" role="status"><InlineSpinner size={18} /> Checking reset link…</div>
      </>
    );
  } else if (complete) {
    content = (
      <>
        {header('bi-check-circle-fill', 'Password Updated', null, 'ok')}
        <div role="status" className="lg-banner ok">
          <i className="bi bi-check-circle-fill" aria-hidden="true" />
          <span>Password reset successfully. You can now sign in with your new password.</span>
        </div>
        <Link to="/login" className="lg-btn"><i className="bi bi-box-arrow-in-right" aria-hidden="true" /> Go to Sign In</Link>
      </>
    );
  } else if (!tokenValid) {
    content = (
      <>
        {header('bi-link-45deg', 'Link Expired', null, 'err')}
        <div role="alert" className="lg-banner err">
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
          <span>{RESET_ERROR}</span>
        </div>
        <Link to="/forgot-password" className="lg-btn"><i className="bi bi-send" aria-hidden="true" /> Request a New Link</Link>
      </>
    );
  } else {
    content = (
      <>
        {header('bi-shield-lock', 'Set a New Password', 'Choose a new password for your TMIS account.')}
        {errorBox}
        <form onSubmit={handleReset} noValidate>
          <div className="lg-field">
            <label htmlFor="student-new-password" className="lg-label">New Password</label>
            <div style={{ position: 'relative' }}>
              <input id="student-new-password" className="lg-input has-toggle" type={showPw ? 'text' : 'password'}
                autoComplete="new-password" placeholder="At least 8 characters" value={form.new_password}
                onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} disabled={loading} />
              <button type="button" className="lg-show" onClick={() => setShowPw(s => !s)}
                aria-label={showPw ? 'Hide passwords' : 'Show passwords'} aria-pressed={showPw}>
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div className="lg-field">
            <label htmlFor="student-confirm-password" className="lg-label">Confirm New Password</label>
            <input id="student-confirm-password" className="lg-input" type={showPw ? 'text' : 'password'}
              autoComplete="new-password" placeholder="Enter the password again" value={form.confirm_password}
              onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))} disabled={loading} />
          </div>
          <div className="lg-actions">
            <button type="submit" className="lg-btn" disabled={loading}>
              {loading
                ? <><InlineSpinner size={15} /> Resetting Password…</>
                : <><i className="bi bi-shield-check" aria-hidden="true" /> Reset Password</>}
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <AuthShell panelTitle="Need a Hand?" panelText="Reset your password in a few quick steps.">
      {content}
      {!complete && (
        <Link to="/login" className="lg-back"><i className="bi bi-arrow-left" aria-hidden="true" /> Back to Sign In</Link>
      )}
    </AuthShell>
  );
}
